import React, { Component, ErrorInfo } from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  errorMessage: string;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, errorMessage: '' };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, errorMessage: error?.message || 'Unknown error' };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2 }}>
          <Paper variant="outlined" sx={{ p: { xs: 4, md: 6 }, maxWidth: 520, textAlign: 'center' }}>
            <BugReportIcon sx={{ fontSize: 64, color: 'rgba(7,24,45,0.15)', mb: 1.5 }} />
            <Typography variant="h5" fontWeight={700} mb={0.75}>
              Something went wrong
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
              An unexpected error occurred. Please refresh the page or go back to try again.
            </Typography>
            {process.env.NODE_ENV === 'development' && (
              <Typography variant="caption" color="error" display="block" mb={2.5} sx={{ fontFamily: 'monospace', bgcolor: '#fff8f8', p: 1, borderRadius: 1 }}>
                {this.state.errorMessage}
              </Typography>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center" mt={2.5}>
              <Button variant="contained" onClick={() => window.location.reload()}>
                Reload page
              </Button>
              <Button variant="outlined" onClick={() => { this.setState({ hasError: false, errorMessage: '' }); window.history.back(); }}>
                Go back
              </Button>
            </Stack>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
